import { cn } from "@/libs/utils";
import Footer from "@/v2-components/footer";
import { createContext, useContext } from "react";
import Image from "next/image";
import { useTrans } from "@/utils/useTrans";
import GlobalNavigationBar from "@/v2-components/global-navigation-bar";
import PromoBanner from "@/components/banner/PromoBanner";
import ReferralBanner from "@/components/banner/ReferralBanner";
import { useLocale } from "@/store/locale-store";
import SuggestLocale from "@/components/SuggestLocale";

interface BaseProps {
  children?: React.ReactNode;
}

interface PageProps {
  theme?: "light" | "dark";
  imageSrc?: string;
}

const PageContext = createContext<PageProps | null>(null);

const usePageContext = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error(
      "PageTemplate compound components cannot be rendered outside the PageTemplate component",
    );
  }
  return context;
};

const Gap = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn("pt-12 laptop:pt-16", className)}>{children}</div>
);

const Max = ({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <div className={cn("mx-auto w-full max-w-screen-max", className)}>
      {children}
    </div>
  );
};

function Root({ imageSrc, theme = "light", children }: PageProps & BaseProps) {
  return (
    <PageContext.Provider value={{ imageSrc, theme }}>
      {children}
    </PageContext.Provider>
  );
}

function Cover({
  className,
  src,
  alt = "",
  children,
  ...props
}: Omit<React.ComponentProps<typeof Image>, "src" | "alt"> & {
  src?: string;
  alt?: string;
}) {
  const { imageSrc } = usePageContext();

  if (children) return <>{children}</>;

  return (
    <Image
      className={cn(className)}
      src={src ?? imageSrc ?? ""}
      alt={alt}
      {...props}
    />
  );
}

function Content({
  children,
  className,
  useCustomNav = false,
  countryCode,
  headerBanner,
}: {
  children?: React.ReactNode;
  className?: string;
  useCustomNav?: boolean;
  countryCode?: string;
  // 템플릿 sticky 헤더(nav + 기본 배너) 아래에 페이지별 추가 배너를 끼우는 슬롯.
  // 전 software 페이지 공용 영역이라, 특정 페이지에서만 띄울 배너는 이 prop 으로 주입한다.
  headerBanner?: React.ReactNode;
}) {
  const locale = useLocale();
  const canSuggeset = !!countryCode && locale.countryCode !== countryCode;

  return (
    <div className={cn("relative w-full", className)}>
      {!useCustomNav && (
        <div className="sticky top-0 z-30">
          {canSuggeset && <SuggestLocale countryCode={countryCode} />}
          <ReferralBanner />
          <GlobalNavigationBar />
          <PromoBanner />
          {headerBanner}
        </div>
      )}
      {children}
      <Footer />
    </div>
  );
}

const Header = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <h2 className={cn("text-3xl text-coolgray-90 laptop:text-5xl", className)}>
      {children}
    </h2>
  );
};

const Badge = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => {
  const { t } = useTrans(["software"]);
  return (
    <span
      className={cn(
        "inline-block rounded-[4px] bg-teal-50 px-2 py-1 font-500 text-teal-10",
        className,
      )}
    >
      {children || t("Beta")}
    </span>
  );
};

const SoftwarePageTemplate = Object.assign(Root, {
  Cover,
  Content,
  Badge,
  Header,
  Gap,
  Max,
});

export default SoftwarePageTemplate;
